/**
 * Vorprüfung eines Imports: was ein neuer Export am Bestand ändert.
 *
 * Die Regeln stehen in der API (`services/importPruefung.ts`); hier nur, was
 * die Maske zum Anzeigen und Entscheiden braucht.
 */
import { hours as formatHours, shortDate } from "./format";

export type Entscheidung = "neu" | "alt";
export type TagStatus = "DRAFT" | "CALCULATED" | "APPROVED" | "LOCKED" | null;

export interface Schicht {
  startTime: string;
  endTime: string | null;
  hours: string | null;
}

export interface Aenderung {
  key: string;
  personalNr: number;
  businessDate: string;
  workArea: string;
  art: "geaendert" | "entfernt";
  alt: Schicht[];
  neu: Schicht[];
  name: string;
  tagStatus: TagStatus;
}

export interface Pruefung {
  gleich: number;
  neu: number;
  aenderungen: Aenderung[];
  kassenAbweichungen: number;
  betroffeneTage: { datum: string; status: "APPROVED" | "LOCKED" }[];
}

/** Ausgezahlte Tage ändert kein Import; dort gibt es nichts zu entscheiden. */
export const entscheidbar = (a: Aenderung): boolean => a.tagStatus !== "LOCKED";

export function offen(aenderungen: readonly Aenderung[], entscheidungen: Readonly<Record<string, Entscheidung>>): number {
  return aenderungen.filter((a) => entscheidbar(a) && !entscheidungen[a.key]).length;
}

/** „6.9" → „6.90", damit alt und neu gleich breit und vergleichbar dastehen. */
function zweiStellen(h: string | null): string | null {
  if (h === null || h === "") return h;
  const [ganz, rest = ""] = h.split(".");
  return `${ganz}.${(rest + "00").slice(0, 2)}`;
}

/** „18:00–22:00 · 4,00 h", ohne Endzeit nur Beginn und Dauer. */
export function schichtText(s: Schicht): string {
  const zeit = s.startTime ? (s.endTime ? `${s.startTime}–${s.endTime}` : `ab ${s.startTime}`) : "ohne Uhrzeit";
  return `${zeit} · ${formatHours(zweiStellen(s.hours))}`;
}

export function tagHinweis(t: { datum: string; status: "APPROVED" | "LOCKED" }): string {
  return t.status === "APPROVED"
    ? `${shortDate(t.datum)} ist freigegeben. Übernimmt der Import dort etwas, wird der Tag wieder geöffnet und muss neu freigegeben werden.`
    : `${shortDate(t.datum)} ist ausgezahlt. Daran ändert der Import nichts mehr: Stunden für diesen Tag werden nicht übernommen, Kassenbelege nur abgelegt.`;
}
